import { HostContext } from "~/constants";

import { getAssetUrl } from "./url";
import { logger } from "./logging";

import type { I18nTranslationFunction } from "~/components/i18n/context";

/**
 * Determines if the app is running in a mobile app webview.
 */
export const isMobileNative = (hostContext?: HostContext) => {
  return Boolean(
    globalThis.window?.BibleProjectApp ??
      hostContext === HostContext.MobileNative,
  );
};

/**
 * Determines if the app is running in a mobile app webview
 * on ios.
 */
export const isIosNative = (platform?: string) => {
  return (platform ?? globalThis.window?.BibleProjectApp?.platform) === "ios";
};

/**
 * Determines if the app is running in a mobile app webview
 * on android.
 */
export const isAndroidNative = (platform?: string) => {
  return (
    (platform ?? globalThis.window?.BibleProjectApp?.platform) === "android"
  );
};

/**
 * Determines if the app is running on an Android device.
 */
export const isAndroid = () => {
  return (
    isAndroidNative() ||
    globalThis?.navigator?.userAgent.toLowerCase().indexOf("android") !== -1
  );
};

/**
 * Determines if the app is running on an iOS device.
 */
export const isIos = () => {
  return (
    isIosNative() ||
    globalThis?.navigator?.userAgent.toLowerCase().indexOf("iphone") !== -1
  );
};

/**
 * Determines if the app is running in a browser (vs a
 * server).
 */
export const isBrowser = () => {
  return typeof globalThis.document !== "undefined";
};

/**
 * Determines if the user's device supports touch gestures.
 */
export const supportsTouch = () => {
  return (
    isBrowser() &&
    globalThis.matchMedia("(hover: none), (pointer: coarse)").matches
  );
};

/**
 * Determines if the app is running in an environment that
 * allows forcing orientation. E.g. the tablet version
 * currently does not.
 */
export const supportsOrientationLock = () => {
  return !!globalThis.window?.BibleProjectApp?.supportsOrientationLock;
};

/**
 * Determines if the user's device supports the Screen Orientation API.
 */
export const supportsScreenOrientation = () => {
  return globalThis.screen && "orientation" in globalThis.screen;
};

/**
 * Retrieves an environment variable value.
 */
export function env(key: keyof EnvironmentVariables, fallback = "") {
  try {
    return (
      globalThis.window?.__env__?.[key] ??
      process.env[key]?.replace(/^"|"$/g, "") ??
      fallback
    );
  } catch (error) {
    return fallback;
  }
}

/**
 * Fetches a remote asset.
 */
export async function fetchAsset(
  path: string,
  signal?: AbortSignal,
): Promise<string> {
  return new Promise((resolve, reject) => {
    fetch(getAssetUrl(path), { signal })
      .then(async (r) => {
        const text = await r.text();
        if (!r.ok) reject(r);
        resolve(text);
      })
      .catch((e) => reject(e));
  });
}

/**
 * Formats seconds as a timestamp string
 */
export function formatTimestamp(seconds = 0) {
  const getHours = (value: number) => Math.trunc((value / 60 / 60) % 60);
  const getMinutes = (value: number) => Math.trunc((value / 60) % 60);
  const getSeconds = (value: number) => Math.trunc(value % 60);

  const leadingZero = (value: number) => `0${value}`.slice(-2);

  const n = Math.max(seconds, 0);
  const hours = getHours(n);
  const mins = getMinutes(n);
  const secs = getSeconds(n);

  return `${hours > 0 ? `${hours}:` : ""}${mins}:${leadingZero(secs)}`;
}

/**
 * Formats a set of numbers as a perctage for the purposes
 * of styling.
 */
export function formatPercentage(value: number, base = 1, round = true) {
  const percentage = Math.min(value / base, 1) * 100;
  if (round) return `${Math.round(percentage)}%`;
  return `${percentage}%`;
}

export function hash(value: string) {
  let hash = 0;
  if (value.length === 0) return hash;
  for (let i = 0; i < value.length; i++) {
    const chr = value.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0;
  }
  return hash;
}

/**
 * Creates a throttled function that is only invoked once
 * per every wait milliseconds.
 */
export function throttle<T extends (...args: any) => any>(fn: T, wait = 100) {
  let baseTime = 0;
  return (...args: unknown[]) => {
    const currentTime = Date.now();
    if (baseTime + wait <= currentTime) {
      baseTime = currentTime;
      fn(...args);
    }
  };
}

/**
 * Creates a debounced function that is only invoked after
 * the wait interval
 */
export function debounce<T extends (...args: any) => any>(func: T, wait = 100) {
  let timer: NodeJS.Timeout;
  return (...args: unknown[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, wait);
  };
}

/**
 * Waits for a number of milliseconds
 */
export function sleep(ms = 1000) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Waits until a condition is met
 */
export function waitUntil<T>(
  predicate: () => T,
  options: { interval?: number; timeout?: number } = {},
) {
  const { interval, timeout } = {
    interval: 500,
    timeout: 30_000,
    ...options,
  };

  let retries = Math.floor(timeout / interval);

  return new Promise<T>((resolve, reject) => {
    function check() {
      try {
        const result = predicate();

        if (result) {
          resolve(result);
          return;
        }

        if (retries > 0) {
          retries--;
          setTimeout(check, interval);
          return;
        }

        reject(new Error(`waitUntil timed out after ${timeout}ms`));
      } catch (error) {
        reject(error);
      }
    }

    check();
  });
}

/**
 * No-op
 */
export function noop() {
  return;
}

/**
 * Generates a random string that can be used as an id in
 * places where the useId will not work.
 */
export function generateId() {
  return Math.random().toString(36).substring(2, 9);
}

/**
 * Localizes a number according to the user's chosen locale.
 */
export function localizeNumber(input: number | string) {
  const n =
    typeof input === "string" ? parseFloat(input.replace(/,/, "")) : input;
  return n.toLocaleString();
}

/**
 * Localizes a date according to the user's chosen locale.
 */
export function localizeDate(
  input: Date | string,
  options?: Intl.DateTimeFormatOptions,
) {
  const n = new Date(input ?? Date.now());
  return n.toLocaleDateString("en-us", options);
}

/**
 * Localizes a date according to the user's chosen locale.
 */
export function localizeTime(
  input: Date | string,
  options: Intl.DateTimeFormatOptions = {
    timeStyle: "short",
  },
) {
  const n = new Date(input ?? Date.now());
  return n.toLocaleTimeString("en-us", options);
}

/**
 * Formats and localizes a content's duration.
 */
export function formatDuration(seconds: number, t: I18nTranslationFunction) {
  const fallbacks = {
    "utils.helpers.hour_one": "{{count}} Hour",
    "utils.helpers.hour_other": "{{count}} Hours",
    "utils.helpers.minute_one": "{{count}} Minute",
    "utils.helpers.minute_other": "{{count}} Minutes",
  };
  const m = Math.floor((seconds / 60) % 60);
  const h = Math.floor(seconds / 3600);
  const v: string[] = [];
  if (h) v.push(t("utils.helpers.hour", { count: h }, fallbacks));
  if (m) v.push(t("utils.helpers.minute", { count: m }, fallbacks));
  return v.join(" ");
}

/**
 * Gets a random integer within a range
 */
export function getRandomInteger(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/**
 * Convert any value to a boolean.
 */
export function toBool(value: unknown) {
  if (typeof value === "boolean") {
    return value;
  }
  if (typeof value === "string") {
    return !(value === "false" || value === "");
  }
  return !!value;
}

/**
 * Rounds a number to a certain precision
 */
export function round(n?: number, precision = 2) {
  return n
    ? Math.round(n * Math.pow(10, precision)) / Math.pow(10, precision)
    : 0;
}

/**
 * Compares two objects for shallow equality.
 */
export function shallowEqual(object1: object, object2: object) {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (
      object1[key as keyof typeof object1] !==
      object2[key as keyof typeof object2]
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Determines if one semver string satisfies another.
 */
export function satisfiesVersion(required: string, actual: string) {
  const parse = (str: string) => {
    const { major, minor, patch } =
      str.match(/^(?<major>[0-9]*).(?<minor>[0-9]*).(?<patch>[0-9]*)/)
        ?.groups ?? {};

    if (!major || !minor || !patch) {
      throw new Error("Invalid semver string");
    }

    return { major: Number(major), minor: Number(minor), patch: Number(patch) };
  };

  try {
    const r = parse(required);
    const a = parse(actual);

    if (a.major > r.major) return true;
    if (a.major < r.major) return false;
    if (a.minor > r.minor) return true;
    if (a.minor < r.minor) return false;
    return a.patch >= r.patch;
  } catch (error) {
    logger.error("satisfiesVersion#parse failed", error);
    return false;
  }
}
